import React from 'react';

import { Button, Drawer } from '../../../design-system/components/desktop';
import {
  ActionRow,
  DetailList,
  DetailRow,
  DetailSection,
  Notice,
  StatusPill,
} from '../../../design-system/patterns/console';
import {
  CartLine,
  CartLines,
  SummaryRow,
  SummaryTotal,
} from '../../../design-system/patterns/cart';
import ReviewFormView from '../../../pages/OrderTracking/OrderTracking1/ReviewForm.view';
import { formatCurrency, mediaUrl } from '../../../utils/common';
import { parseRand, shortOrderId } from '../../Dashboard/dashboard.parts';

import '../../Dashboard/dashboard.css';

/**
 * The order details drawer, the view. Pure.
 *
 * THE FOURTH LINE-ITEM SURFACE
 * ----------------------------
 * `patterns/cart` was built for exactly four surfaces: `/cart`, the cart
 * drawer, the checkout summary, and this one. This file was the fourth, and it
 * was reaching across two directories to `import '../../Cart/CartOverlay/CartOverlay.css'`
 * so it could borrow the drawer's class names. It uses `CartLine` in `readOnly`
 * `compact` mode now, and that stylesheet and the `.cart-view-btn` block in
 * `legacy-overrides.css` that existed only for this file both went with it.
 *
 * NO SHIPPING PRICE IS NAMED, DELIBERATELY
 * ----------------------------------------
 * The old breakdown rendered a "Shipping" row on both the seller and the buyer
 * side. The standing product rule is that we never name a courier, an origin
 * city or a shipping price, because we cannot pull that data, and a figure this
 * file DERIVED by subtracting three numbers from a fourth is the least
 * defensible place to break it. The rows that remain are all facts the API
 * states: the items subtotal, the platform fee at the rate system settings
 * reports, the seller's profit, and the total the buyer actually paid. The
 * total is the API's own number, not a sum of the rows above it, which is why
 * it is labelled as what the buyer paid and carries a note saying delivery is
 * inside it. "Bought in person" stays, because that is a property of the order
 * and not a price.
 *
 * SHIPMENT STATUS IS RENDERED THROUGH `StatusPill`, so it reads from
 * `utils/orderStatus.js` and gets one of the four real tones plus primary on
 * `approved`. "Shipped" is not a value in this app and is not introduced here.
 *
 * DESTRUCTIVE AND IRREVERSIBLE COMMANDS ARE GHOST BUTTONS whose labels carry
 * the consequence. There is no `.dsk-btn--danger`.
 */

// Mirrors apps/api/apps/orders/services.py SHIPMENT_TERMINAL_BAD, the only
// submission_status values the resubmit-shipment endpoint accepts.
const SHIPMENT_TERMINAL_BAD = new Set(['no-rates', 'failed-indefinitely']);

/** The app's own sentinel for an order that never entered the delivery flow. */
const IN_PERSON = 'Bought in-person';

export function OrderDetailsDrawerView({
  open = false,
  onClose = () => {},
  order,
  // Fail closed, like the container: the seller footer owns "Confirm order",
  // which creates a shipping label. An omitted prop must hide it, not offer it.
  seller = false,
  isAdmin = false,

  priceBreakdown,

  onConfirmOrder,
  onTrackOrder,
  onDownloadWaybill,

  onResubmitShipment,
  resubmitBusy = false,

  reviewRating = 0,
  onReviewRatingChange,
  reviewText = '',
  onReviewTextChange,
  onReviewSubmit,
  reviewBusy = false,
}) {
  if (!order) return null;

  const items = Array.isArray(order.items) ? order.items : [];
  const trackingId = order.tracking_id || order.trackingId || '';
  const shipmentStatus = order.shipment_status;
  const orderId = order.order_id || order.orderId || order.id || '';
  const inPerson = Boolean(order.bought_in_person || order.is_in_person);
  const canResubmit = isAdmin && SHIPMENT_TERMINAL_BAD.has(order.submission_status);

  const footer = seller ? (
    <ActionRow>
      <Button variant="ghost" onClick={onClose}>
        Close
      </Button>
      {shipmentStatus !== IN_PERSON && trackingId ? (
        <Button variant="ghost" onClick={onDownloadWaybill}>
          Download waybill
        </Button>
      ) : null}
      {shipmentStatus !== IN_PERSON ? (
        <Button
          variant="primary"
          onClick={!trackingId ? onConfirmOrder : onTrackOrder}
          disabled={isAdmin && !trackingId}
        >
          {!trackingId ? (isAdmin ? 'Booking pending' : 'Confirm order') : 'Track order'}
        </Button>
      ) : null}
    </ActionRow>
  ) : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker={`Order ${shortOrderId(orderId)}`}
      title={seller ? order.buyer || 'Order details' : order.seller || 'Order details'}
      footer={footer}
    >
      <DetailSection>
        <DetailList>
          <DetailRow label="Order ID" value={orderId} />
          <DetailRow label="Tracking ID" value={trackingId || 'Not assigned'} />
          <DetailRow label={seller ? 'Buyer' : 'Seller'} value={seller ? order.buyer : order.seller} />
          <DetailRow
            label="Payment"
            value={<StatusPill status={order.order_status} />}
            keepEmpty
          />
          <DetailRow
            label="Shipment"
            value={
              inPerson ? (
                'Bought in person'
              ) : order.shipment_status ? (
                <StatusPill status={order.shipment_status} />
              ) : (
                'Not booked yet'
              )
            }
            keepEmpty
          />
          <DetailRow
            label="Delivery"
            value={order.is_delivered ? 'Delivered' : 'Not delivered yet'}
            keepEmpty
          />
        </DetailList>
      </DetailSection>

      {canResubmit ? (
        <DetailSection>
          <Notice title="Shipment booking failed.">
            {order.submission_failed_reason
              ? order.submission_failed_reason
              : 'The courier booking did not go through.'}
          </Notice>
          <ActionRow start style={{ marginTop: 'var(--ds-space-3, 12px)' }}>
            <Button variant="ghost" onClick={onResubmitShipment} disabled={resubmitBusy}>
              {resubmitBusy ? 'Re-submitting' : 'Re-submit shipment'}
            </Button>
          </ActionRow>
        </DetailSection>
      ) : null}

      <DetailSection title="Purchased items">
        {items.length === 0 ? (
          <p className="dsk-hint">No items are recorded on this order.</p>
        ) : (
          <CartLines>
            {items.map((item, index) => {
              const unit = parseRand(item.price);
              return (
                <CartLine
                  key={`${item.card || 'item'}-${index}`}
                  readOnly
                  compact
                  image={item.image_url ? mediaUrl(item.image_url) : undefined}
                  imageAlt=""
                  name={item.card || 'Unknown card'}
                  cardNumber={item.code || item.card_code}
                  condition={item.condition}
                  unitPrice={formatCurrency(unit)}
                  quantity={item.quantity || 1}
                  lineTotal={formatCurrency(unit * (item.quantity || 1))}
                />
              );
            })}
          </CartLines>
        )}
      </DetailSection>

      {priceBreakdown ? (
        <DetailSection title={seller ? 'What this order earned you' : 'What you paid'}>
          <SummaryRow label="Items subtotal" value={formatCurrency(priceBreakdown.subtotal)} />
          {/* WHAT DELIVERY COST, on the buyer's side only.
              Without it the panel put a subtotal above a total and left the
              difference to a sentence -- on QA, R1.68 of cards under a R127.95
              total. The seller's panel answers "what this order earned you",
              and delivery is not earnings, so it does not appear there.

              Rendered only when the API stated a figure AND that figure is
              more than zero: an in-person order never entered the shipping
              branch at checkout, so its delivery is exactly R0.00 and a row
              saying so is noise the in-person note below already covers. A
              `null` means nobody told us, which is not the same as free. */}
          {!seller && priceBreakdown.shipping > 0 ? (
            <SummaryRow label="Delivery" value={formatCurrency(priceBreakdown.shipping)} />
          ) : null}
          {seller ? (
            <>
              <SummaryRow
                label={`Platform fee (${priceBreakdown.platformFeePercentage}%)`}
                value={formatCurrency(priceBreakdown.fees)}
              />
              <SummaryRow label="Your profit" value={formatCurrency(priceBreakdown.profit)} />
            </>
          ) : null}
          <SummaryTotal
            label={seller ? 'Buyer paid' : 'Total'}
            value={formatCurrency(priceBreakdown.total)}
          />
          {/* The sentence is only needed while something is unaccounted for.
              Once a Delivery row states the difference, "the total also covers
              nationwide delivery" is repeating what the rows already add up
              to. It still earns its place on the two occasions the rows cannot
              speak: an in-person order, and a total the API sent no delivery
              figure for. */}
          {inPerson ? (
            <p className="dsk-hint">
              This order was bought in person, so nothing was delivered.
            </p>
          ) : !seller && priceBreakdown.shipping > 0 ? null : (
            <p className="dsk-hint">The total also covers nationwide delivery.</p>
          )}
        </DetailSection>
      ) : null}

      {!seller ? (
        <DetailSection title="Review this order">
          {order.has_rating ? (
            <p className="dsk-hint">You have already reviewed this order.</p>
          ) : (
            <ReviewFormView
              orderId={orderId}
              rating={reviewRating}
              onRatingChange={onReviewRatingChange}
              review={reviewText}
              onReviewChange={onReviewTextChange}
              loading={reviewBusy}
              onSubmit={onReviewSubmit}
            />
          )}
        </DetailSection>
      ) : null}
    </Drawer>
  );
}

export default OrderDetailsDrawerView;
